import type {
  AraClarifyOption,
  AraEditFrame,
  AraEditTarget,
  AraRemoveFrame
} from '@/features/ara-generation/types/ara-events'

export type ChatAssistantProposal = AraEditFrame

/** A clarify answer waiting on the user: which of the equally-matching targets
 * the request meant. `answeredId` marks the option the user picked so the
 * buttons render as done. */
export type ChatClarify = {
  request: string
  options: ChatClarifyOption[]
  answeredId: string | null
}

export type ChatClarifyOption = AraClarifyOption

/** `confirmed`/`cancelled` are terminal: the decision stays in the transcript so
 *  the card reads as decided rather than vanishing. */
export type ChatSectorRebuildStatus = 'pending' | 'confirmed' | 'cancelled'

export type ChatSectorRebuild = {
  sector: string
  status: ChatSectorRebuildStatus
}

export type ChatProposalStatus = 'idle' | 'previewing' | 'accepted' | 'rejected'
export type ChatTargetProposalStatus = ChatProposalStatus

export type ChatSectionProposal = {
  kind: 'section'
  frame: AraEditFrame
  status: ChatProposalStatus
}

export type ChatTargetProposal = {
  kind: 'target'
  targetKey: string
  html: string
  target: AraEditTarget
  status: ChatProposalStatus
}

export type ChatSpanProposal = {
  kind: 'span'
  targetKey: string
  text: string
  target: AraEditTarget
  status: ChatProposalStatus
}

export type ChatChartProposal = {
  kind: 'chart'
  chartKey: string
  chartId: string
  patch: Record<string, unknown>
  target?: AraEditTarget
  status: ChatProposalStatus
}

export function isChartProposal(proposal: {
  kind?: string
}): proposal is ChatChartProposal & { kind: 'chart' } {
  return proposal.kind === 'chart'
}

export type ChatRemoveProposal = {
  kind: 'remove'
  frame: AraRemoveFrame
  status: ChatProposalStatus
}

export type ChatUserMessage = {
  id: string
  role: 'user'
  content: string
}

export type ChatAssistantMessage = {
  id: string
  role: 'assistant'
  response: string
  streaming: boolean
  error: string | null
  proposals: ChatSectionProposal[]
  targetedProposals: ChatTargetProposal[]
  spanProposals: ChatSpanProposal[]
  chartProposals: ChatChartProposal[]
  /**
   * Candidate cohorts offered when the SE asks to change the sector, best fit
   * first. Not a proposal: there is nothing to accept or reject, only a choice
   * to make, and choosing starts a full rebuild rather than patching the
   * document. Absent on every other message.
   */
  sectorOptions?: string[]
  /**
   * A cohort the assistant committed to, held pending the SE's confirmation.
   * Confirming discards the current draft and spends ~3 minutes rebuilding, so
   * the frame is never acted on when it arrives — the model proposes and the SE
   * commits. Also set when an SE clicks a `sectorOptions` button with unsaved
   * work in the draft, so one click can never silently throw that work away.
   */
  sectorRebuild?: ChatSectorRebuild
  /**
   * The reply redirected a scope ask to the Scope panel: no edit accompanies it,
   * only a button that opens the panel. Absent on every other message.
   */
  scopeRedirect?: boolean
  removeProposals: ChatRemoveProposal[]
  clarify: ChatClarify | null
}

export function hasAssistantMessageContent(message: ChatAssistantMessage): boolean {
  return (
    message.response.trim().length > 0 ||
    Boolean(message.error?.trim()) ||
    message.proposals.length > 0 ||
    message.targetedProposals.length > 0 ||
    message.spanProposals.length > 0 ||
    message.chartProposals.length > 0 ||
    Boolean(message.clarify && message.clarify.options.length > 0) ||
    (message.sectorOptions?.length ?? 0) > 0 ||
    // A reply whose only content is the rebuild offer must still render, or the
    // SE is left with a silent message and no way to confirm.
    Boolean(message.sectorRebuild) ||
    message.removeProposals.length > 0 ||
    Boolean(message.scopeRedirect)
  )
}

export type ChatMessage = ChatUserMessage | ChatAssistantMessage
